import { AppBreadcrumb, AppContainer } from '@/components/atoms';
import {
  AdminInsurerAttentionCreateForm,
  AdminInsurerAttentionCreateTable,
  AdminInsurerAttentionFilter,
} from '@/components/organisms';
import {
  useAdminInsurerAttentionInit,
  useAdminInsurerAttentionStore,
  useAdminInsurerList,
} from '@/hooks/admin';
import type { UploadFieldValue } from '@/components/organisms/PresignedUploadField';
import { InsurerAttentionCreateFormData } from '@/schemas/insurerAttentionCreateSchema';
import { Insurer } from '@/types/admin-insurer';
import { AdminInsurerAttentionFilterParams } from '@/types/admin-insurer-attention';
import { SorterResult } from 'antd/es/table/interface';
import { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const LIST_PER_PAGE = 100;
const MAX_ATTACHMENTS = 5;


const SiteAdminInsurerAttentionCreatePage = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tableRef = useRef<HTMLDivElement>(null);

  const [attachments, setAttachments] = useState<(UploadFieldValue | null)[]>(
    Array(MAX_ATTACHMENTS).fill(null)
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const listPage = searchParams.get('page') || '1';
  const sortBy = searchParams.get('sort_by') || undefined;
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;

  // Map AdminInsurerAttentionFilter params → InsurerQuery params
  const insurerQuery = {
    page: listPage,
    per_page: String(LIST_PER_PAGE),
    sort_by: sortBy,
    order: order || undefined,
    search_insurer_number: searchParams.get('insurer_number')?.replace(/-\d+$/, '') || undefined,
    search_insurer_name: searchParams.get('insurer_name') || undefined,
    search_insurer_name_kana: searchParams.get('insurer_name_kana') || undefined,
    search_insurer_type_id: searchParams.get('insurer_type_id') || undefined,
    prefecture_id: searchParams.get('prefecture_id') || undefined,
  };

  const { data: initData } = useAdminInsurerAttentionInit();
  const { data: listData, isLoading: isListLoading } = useAdminInsurerList(insurerQuery);
  const { mutate: storeRecords, isPending: isSubmitting } = useAdminInsurerAttentionStore();

  const prefectureOptions =
    initData?.prefectures?.map(p => ({ value: p.id, label: p.name })) ?? [];

  const breadcrumbItems = [
    { title: '県別注意事項', href: `/${currentSite}/admin/insurer_attentions` },
    { title: '新規' },
  ];

  const handleSubmit = (data: InsurerAttentionCreateFormData) => {
    storeRecords({
      prefecture_id: data.prefecture_id,
      publish_date: data.publish_date,
      remarks: data.remarks,
      insurer_ids: selectedRowKeys,
      attachments: attachments.some(Boolean) ? attachments : undefined,
    });
  };

  const handleTableChange = (
    sorter: SorterResult<Insurer> | SorterResult<Insurer>[]
  ) => {
    const params = new URLSearchParams(searchParams);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s.order) {
      params.set('sort_by', (s.columnKey || s.field) as string);
      params.set('order', s.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSearch = (filters: AdminInsurerAttentionFilterParams) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    params.set('page', '1');
    setSearchParams(params);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <AppContainer className="py-6 space-y-5">
      <AppBreadcrumb items={breadcrumbItems} />
      <h3 className="text-xl">県別注意事項作成</h3>

      <AdminInsurerAttentionFilter
        showCsv={false}
        onBack={() => navigate(`/${currentSite}/admin/insurer_attentions`)}
        onSearch={handleSearch}
      />

      <AdminInsurerAttentionCreateForm
        prefectureOptions={prefectureOptions}
        isSubmitting={isSubmitting}
        maxAttachments={MAX_ATTACHMENTS}
        selectedCount={selectedRowKeys.length}
        onAttachmentsChange={setAttachments}
        onSubmit={handleSubmit}
      />

      <div ref={tableRef}>
        <AdminInsurerAttentionCreateTable
          data={listData?.data ?? []}
          meta={listData?.meta}
          isLoading={isListLoading}
          selectedRowKeys={selectedRowKeys}
          sortBy={sortBy}
          order={order}
          pageSize={LIST_PER_PAGE}
          onSelectionChange={setSelectedRowKeys}
          onTableChange={handleTableChange}
          onPageChange={page => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(page));
            setSearchParams(params);
          }}
        />
      </div>
    </AppContainer>
  );
};

export default SiteAdminInsurerAttentionCreatePage;
